import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Typography,
  Space,
  message,
  Alert,
  Popconfirm,
  Switch,
  DatePicker,
  Row,
  Col,
  Tooltip,
  Grid,
  Dropdown
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ClearOutlined,
  ImportOutlined,
  CodeOutlined,
  MoreOutlined,
  FilterOutlined
} from '@ant-design/icons';
import { deviceAPI, categoryAPI, policyAPI, versionAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import {
  Device,
  Category,
  Policy,
  Version,
  RegisterDeviceRequest,
  UpdateDeviceRequest
} from '../types';
import DeviceImport from '../components/DeviceImport';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

const Devices: React.FC = () => {
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [devices, setDevices] = useState<Device[]>([]);
  const [filteredDevices, setFilteredDevices] = useState<Device[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [versions, setVersions] = useState<Version[]>([]);
  const [showSearchForm, setShowSearchForm] = useState(!isMobile);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [editingDevice, setEditingDevice] = useState<Device | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();

  // 搜索条件状态
  const [searchFilters, setSearchFilters] = useState({
    id: '',
    deviceCode: '',
    categoryId: '',
    userIdentity: '',
    description: '',
    policyId: '',
    versionId: '',
    status: '',
    dateRange: null as any
  });

  useEffect(() => {
    fetchData();
  }, []);

  // 当devices数据变化时，重新应用过滤器
  useEffect(() => {
    applyFilters(searchFilters);
  }, [devices]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [devicesData, categoriesData, policiesData, versionsData] = await Promise.all([
        deviceAPI.getDevices(),
        categoryAPI.getCategoryTree(), // 使用树形结构API
        policyAPI.getPolicies(),
        versionAPI.getVersions()
      ]);
      setDevices(devicesData);
      setFilteredDevices(devicesData); // 初始化过滤后的数据
      setCategories(categoriesData);
      setPolicies(policiesData);
      setVersions(versionsData);
    } catch (err: any) {
      setError(err.message || '获取数据失败');
      console.error('获取数据失败:', err);
    } finally {
      setLoading(false);
    }
  };

  // 搜索过滤逻辑
  const applyFilters = (filters: typeof searchFilters) => {
    let filtered = [...devices];

    // ID过滤
    if (filters.id) {
      filtered = filtered.filter(device =>
        device.id.toString().includes(filters.id)
      );
    }

    // 设备代码过滤
    if (filters.deviceCode) {
      filtered = filtered.filter(device =>
        device.deviceCode.toLowerCase().includes(filters.deviceCode.toLowerCase())
      );
    }

    // 目录过滤
    if (filters.categoryId) {
      filtered = filtered.filter(device =>
        device.categoryId === parseInt(filters.categoryId)
      );
    }

    // 用户身份过滤
    if (filters.userIdentity) {
      filtered = filtered.filter(device =>
        (device.userIdentity || '').toLowerCase().includes(filters.userIdentity.toLowerCase())
      );
    }

    // 描述过滤
    if (filters.description) {
      filtered = filtered.filter(device =>
        (device.description || '').toLowerCase().includes(filters.description.toLowerCase())
      );
    }

    // 管控措施过滤
    if (filters.policyId) {
      filtered = filtered.filter(device => {
        // 如果设备没有管控措施（policyId为null/undefined），则不匹配
        if (!device.policyId) return false;
        // 将搜索条件转换为数字进行比较
        return device.policyId === parseInt(filters.policyId);
      });
    }

    // 版本过滤
    if (filters.versionId) {
      filtered = filtered.filter(device => {
        // 如果设备没有版本（versionId为null/undefined），则不匹配
        if (!device.versionId) return false;
        // 将搜索条件转换为数字进行比较
        return device.versionId === parseInt(filters.versionId);
      });
    }

    // 状态过滤
    if (filters.status) {
      filtered = filtered.filter(device => {
        const status = getDeviceStatusValue(device);
        return status === filters.status;
      });
    }

    // 日期范围过滤
    if (filters.dateRange && filters.dateRange.length === 2) {
      const [startDate, endDate] = filters.dateRange;
      filtered = filtered.filter(device => {
        if (!device.lastCommunicationTime) return false;
        const commTimeLocal = dayjs.utc(device.lastCommunicationTime).local();
        return commTimeLocal.isAfter(startDate.startOf('day')) &&
               commTimeLocal.isBefore(endDate.endOf('day'));
      });
    }

    setFilteredDevices(filtered);
  };

  // 获取设备有效策略的 queryPolicyInterval（秒），优先设备策略，否则从目录继承
  const getDeviceQueryInterval = (device: Device): number => {
    // 优先使用设备自身的策略
    let policyId = device.policyId;
    
    // 如果设备没有策略，尝试从目录链向上查找
    if (!policyId && device.categoryId) {
      const findPolicyFromCategory = (catId: number): number | undefined => {
        const category = categories.find(c => c.id === catId);
        if (!category) return undefined;
        if (category.policyId) return category.policyId;
        if (category.parentId) return findPolicyFromCategory(category.parentId);
        return undefined;
      };
      policyId = findPolicyFromCategory(device.categoryId);
    }
    
    if (policyId) {
      const policy = policies.find(p => p.id === policyId);
      if (policy?.value) {
        try {
          const parsed = JSON.parse(policy.value);
          const interval = parseInt(parsed?.strategies?.queryPolicyInterval, 10);
          if (!isNaN(interval) && interval > 0) {
            return interval;
          }
        } catch (e) {
          // JSON 解析失败，使用默认值
        }
      }
    }
    
    return 600; // 默认 600 秒
  };

  // 获取设备状态值（用于过滤）
  // 规则：
  // - 在线：当前时间 < 最后通信时间 + 策略查询间隔
  // - 最近活跃：24小时内（但不满足在线条件）
  // - 离线：超过24小时
  // - 已禁用：device.disabled = true
  const getDeviceStatusValue = (device: Device) => {
    if (device.disabled) return 'disabled';

    if (device.lastCommunicationTime) {
      const lastCommLocal = dayjs.utc(device.lastCommunicationTime).local();
      const now = dayjs();
      const queryIntervalSeconds = getDeviceQueryInterval(device);
      const expectedNextComm = lastCommLocal.add(queryIntervalSeconds, 'second');
      
      // 在线：当前时间 < 最后通信时间 + 策略查询间隔
      if (now.isBefore(expectedNextComm)) return 'online';
      
      // 最近活跃：24小时内
      const hoursSinceComm = now.diff(lastCommLocal, 'hour');
      if (hoursSinceComm < 24) return 'recent';
    }

    return 'offline';
  };

  // 处理搜索
  const handleSearch = (values: any) => {
    const filters = {
      id: values.id || '',
      deviceCode: values.deviceCode || '',
      categoryId: values.categoryId || '',
      userIdentity: values.userIdentity || '',
      description: values.description || '',
      policyId: values.policyId || '',
      versionId: values.versionId || '',
      status: values.status || '',
      dateRange: values.dateRange || null
    };
    setSearchFilters(filters);
    applyFilters(filters);
  };

  // 重置搜索
  const handleResetSearch = () => {
    const emptyFilters = {
      id: '',
      deviceCode: '',
      categoryId: '',
      userIdentity: '',
      description: '',
      policyId: '',
      versionId: '',
      status: '',
      dateRange: null
    };
    setSearchFilters(emptyFilters);
    searchForm.resetFields();
    setFilteredDevices(devices);
  };

  const showModal = (device?: Device) => {
    setEditingDevice(device || null);
    setModalVisible(true);

    // 使用 setTimeout 确保 Modal 完全渲染后再设置表单值
    setTimeout(() => {
      if (device) {
        // 编辑模式
        form.setFieldsValue({
          deviceCode: device.deviceCode,
          categoryId: device.categoryId,
          policyId: device.policyId,
          versionId: device.versionId,
          userIdentity: device.userIdentity,
          description: device.description,
          disabled: device.disabled === 1,
        });
      } else {
        // 新增模式
        form.resetFields();
      }
    }, 0);
  };

  const handleCancel = () => {
    setModalVisible(false);
    setEditingDevice(null);
    form.resetFields();
  };

  const handleSubmit = async (values: any) => {
    try {
      setSubmitting(true);

      // 将disabled布尔值转换为数字
      const formData: any = {
        ...values,
        disabled: values.disabled ? 1 : 0,
      };

      // 当用户在编辑设备时清空了版本/策略下拉，需要显式传 null 给后端以清空数据库字段
      if (editingDevice) {
        if (editingDevice.policyId != null && values.policyId === undefined) {
          formData.policyId = null;
        }
        if (editingDevice.versionId != null && values.versionId === undefined) {
          formData.versionId = null;
        }
      }

      if (editingDevice) {
        // 更新设备
        const updateData: UpdateDeviceRequest = {
          id: editingDevice.id,
          ...formData
        };
        await deviceAPI.updateDevice(updateData);
        message.success('设备更新成功');
      } else {
        // 注册新设备
        const registerData: RegisterDeviceRequest = formData;
        await deviceAPI.registerDevice(registerData);
        message.success('设备注册成功');
      }

      setModalVisible(false);
      setEditingDevice(null);
      form.resetFields();
      await fetchData();
    } catch (err: any) {
      message.error(err.response?.data?.error || '操作失败');
      console.error('操作失败:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deviceAPI.deleteDevice(id);
      message.success('设备删除成功');
      await fetchData();
    } catch (err: any) {
      message.error(err.message || '删除失败');
      console.error('删除失败:', err);
    }
  };

  const handleImportSuccess = () => {
    setImportModalVisible(false);
    fetchData(); // 刷新设备列表
  };

  const getDeviceStatus = (device: Device) => {
    const status = getDeviceStatusValue(device);
    switch (status) {
      case 'online':
        return <Tag color="green">在线</Tag>;
      case 'recent':
        return <Tag color="orange">最近活跃</Tag>;
      case 'disabled':
        return <Tag color="red">已禁用</Tag>;
      case 'offline':
      default:
        return <Tag color="red">离线</Tag>;
    }
  };

  const getCategoryName = (categoryId: number) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : `目录ID: ${categoryId}`;
  };

  // 获取目录的完整层级路径
  const getCategoryFullPath = (categoryId: number) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? getCategoryPath(category, categories) : `目录ID: ${categoryId}`;
  };

  // 构建层级显示的目录路径
  const getCategoryPath = (category: Category, allCategories: Category[]): string => {
    const path = [category.name];
    let current = category;

    while (current.parentId) {
      const parent = allCategories.find(c => c.id === current.parentId);
      if (parent) {
        path.unshift(parent.name);
        current = parent;
      } else {
        break;
      }
    }

    const result = path.join(' / ');
    console.log(`Category ${category.id} (${category.name}) path:`, result);
    return result;
  };

  // 扁平化目录树，用于选择器显示
  const flattenCategories = (categories: Category[]): Category[] => {
    const result: Category[] = [];

    const flatten = (items: Category[], parentId?: number) => {
      items
        .filter(item => {
          if (parentId === undefined) {
            return item.parentId === null || item.parentId === undefined;
          }
          return item.parentId === parentId;
        })
        .forEach(item => {
          result.push(item);
          flatten(items, item.id);
        });
    };

    flatten(categories);
    return result;
  };

  const getPolicyName = (policyId?: number) => {
    if (!policyId) return '-';
    const policy = policies.find(p => p.id === policyId);
    return policy ? policy.name : `策略ID: ${policyId}`;
  };

  const getVersionName = (versionId?: number) => {
    if (!versionId) return '-';
    const version = versions.find(v => v.id === versionId);
    return version ? `v${version.versionCode}` : `版本ID: ${versionId}`;
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a: Device, b: Device) => a.id - b.id,
    },
    {
      title: '设备代码',
      dataIndex: 'deviceCode',
      key: 'deviceCode',
      width: 165,
      sorter: (a: Device, b: Device) => a.deviceCode.localeCompare(b.deviceCode),
    },
    {
      title: '所属目录',
      dataIndex: 'categoryId',
      key: 'categoryId',
      render: (categoryId: number) => getCategoryFullPath(categoryId),
      width: 200,
      sorter: (a: Device, b: Device) => {
        const pathA = getCategoryFullPath(a.categoryId);
        const pathB = getCategoryFullPath(b.categoryId);
        return pathA.localeCompare(pathB);
      },
    },
    {
      title: '用户身份',
      dataIndex: 'userIdentity',
      key: 'userIdentity',
      render: (text: string) => text || '-',
      width: 120,
      sorter: (a: Device, b: Device) => {
        const userA = a.userIdentity || '';
        const userB = b.userIdentity || '';
        return userA.localeCompare(userB);
      },
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => text || '-',
      width: 150,
      ellipsis: true, // 超长文本显示省略号
      sorter: (a: Device, b: Device) => {
        const descA = a.description || '';
        const descB = b.description || '';
        return descA.localeCompare(descB);
      },
    },
    {
      title: '管控措施',
      dataIndex: 'policyId',
      key: 'policyId',
      render: (policyId: number) => getPolicyName(policyId),
      sorter: (a: Device, b: Device) => {
        const policyA = getPolicyName(a.policyId);
        const policyB = getPolicyName(b.policyId);
        return policyA.localeCompare(policyB);
      },
    },
    {
      title: '版本',
      dataIndex: 'versionId',
      key: 'versionId',
      render: (versionId: number) => getVersionName(versionId),
      sorter: (a: Device, b: Device) => {
        const versionA = getVersionName(a.versionId);
        const versionB = getVersionName(b.versionId);
        return versionA.localeCompare(versionB);
      },
    },
    {
      title: '实际版本',
      dataIndex: 'currentVersionCode',
      key: 'currentVersionCode',
      width: 100,
      render: (code: number | undefined, record: Device) => {
        if (!code) return <Tag>-</Tag>;
        // 获取配置的目标版本
        const targetVersion = versions.find(v => v.id === record.versionId);
        const targetCode = targetVersion?.versionCode;
        // 比较实际版本与目标版本
        if (targetCode && code < targetCode) {
          return <Tooltip title={`需要升级到 v${targetCode}`}><Tag color="orange">v{code}</Tag></Tooltip>;
        } else if (targetCode && code > targetCode) {
          return <Tooltip title={`高于目标版本 v${targetCode}`}><Tag color="blue">v{code}</Tag></Tooltip>;
        }
        return <Tag color="green">v{code}</Tag>;
      },
      sorter: (a: Device, b: Device) => {
        const codeA = a.currentVersionCode || 0;
        const codeB = b.currentVersionCode || 0;
        return codeA - codeB;
      },
    },
    {
      title: '状态',
      key: 'status',
      render: (_: any, record: Device) => getDeviceStatus(record),
      sorter: (a: Device, b: Device) => {
        // 状态排序：在线 > 最近活跃 > 离线 > 已禁用
        const getStatusPriority = (device: Device) => {
          const status = getDeviceStatusValue(device);
          switch (status) {
            case 'online': return 3;
            case 'recent': return 2;
            case 'offline': return 1;
            case 'disabled': return 0;
            default: return 0;
          }
        };

        return getStatusPriority(b) - getStatusPriority(a); // 降序排列，在线设备在前
      },
    },
    {
      title: '最后通信',
      dataIndex: 'lastCommunicationTime',
      key: 'lastCommunicationTime',
      render: (text: string) => text ? (
        isMobile 
          ? dayjs.utc(text).local().format('MM-DD HH:mm')
          : dayjs.utc(text).local().format('YYYY-MM-DD HH:mm:ss')
      ) : '-',
      sorter: (a: Device, b: Device) => {
        if (!a.lastCommunicationTime && !b.lastCommunicationTime) return 0;
        if (!a.lastCommunicationTime) return 1;
        if (!b.lastCommunicationTime) return -1;
        return dayjs.utc(a.lastCommunicationTime).unix() - dayjs.utc(b.lastCommunicationTime).unix();
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: isMobile ? 80 : 220,
      fixed: 'right' as const,
      render: (_: any, record: Device) => {
        // 移动端使用下拉菜单
        if (isMobile) {
          const menuItems = [
            ...(user?.userType === 'super_admin' || user?.userType === 'admin' ? [{
              key: 'console',
              icon: <CodeOutlined />,
              label: '控制台',
              disabled: getDeviceStatusValue(record) !== 'online',
              onClick: () => navigate(`/shell/${record.deviceCode}`)
            }] : []),
            {
              key: 'edit',
              icon: <EditOutlined />,
              label: '编辑',
              onClick: () => showModal(record)
            },
            ...(user?.userType === 'super_admin' || user?.userType === 'admin' ? [{
              key: 'delete',
              icon: <DeleteOutlined />,
              label: '删除',
              danger: true,
              onClick: () => {
                Modal.confirm({
                  title: '确定要删除这个设备吗？',
                  onOk: () => handleDelete(record.id),
                  okText: '确定',
                  cancelText: '取消'
                });
              }
            }] : [])
          ];
          return (
            <Dropdown menu={{ items: menuItems }} trigger={['click']}>
              <Button type="text" icon={<MoreOutlined />} size="small" />
            </Dropdown>
          );
        }
        
        // 桌面端保持原有按钮
        return (
          <Space>
            {(user?.userType === 'super_admin' || user?.userType === 'admin') && (
              <Tooltip title={getDeviceStatusValue(record) !== 'online' ? '仅支持在线设备' : ''}>
                <Button
                  type="link"
                  icon={<CodeOutlined />}
                  onClick={() => navigate(`/shell/${record.deviceCode}`)}
                  size="small"
                  disabled={getDeviceStatusValue(record) !== 'online'}
                >
                  控制台
                </Button>
              </Tooltip>
            )}
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => showModal(record)}
              size="small"
            >
              编辑
            </Button>
            {(user?.userType === 'super_admin' || user?.userType === 'admin') && (
              <Popconfirm
                title="确定要删除这个设备吗？"
                onConfirm={() => handleDelete(record.id)}
                okText="确定"
                cancelText="取消"
              >
                <Button
                  type="link"
                  icon={<DeleteOutlined />}
                  danger
                  size="small"
                >
                  删除
                </Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  const { user } = useAuth();

  if (error) {
    return (
      <Alert
        message="加载失败"
        description={error}
        type="error"
        showIcon
        style={{ margin: '20px 0' }}
      />
    );
  }

  const canRegister = user?.userType === 'super_admin' || user?.userType === 'admin';
  const canImport = user?.userType === 'super_admin' || user?.userType === 'admin';

  // 移动端获取可见列（隐藏部分列）
  const mobileColumns = columns.filter(col => 
    !['所属目录', '用户身份', '描述', '管控措施', '版本', '实际版本'].includes(col.title as string)
  );

  return (
    <div>
      <div className="page-header">
        <Title level={2} style={{ margin: 0 }}>
          📱 设备列表
        </Title>
        <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: isMobile ? 12 : 14 }}>
          管理所有注册的设备信息
        </p>
      </div>

      <Card
        className="content-card"
        title={isMobile ? null : undefined}
        extra={
          <Space wrap size={isMobile ? 'small' : 'middle'}>
            {isMobile && (
              <Button
                icon={<FilterOutlined />}
                onClick={() => setShowSearchForm(!showSearchForm)}
                type={showSearchForm ? 'primary' : 'default'}
              >
                筛选
              </Button>
            )}
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchData}
              loading={loading}
            >
              {isMobile ? '' : '刷新'}
            </Button>
            {canRegister && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => showModal()}
              >
                {isMobile ? '新增' : '注册新设备'}
              </Button>
            )}
            {canImport && (
              <Button
                icon={<ImportOutlined />}
                onClick={() => setImportModalVisible(true)}
              >
                {isMobile ? '导入' : '批量导入'}
              </Button>
            )}
          </Space>
        }
      >
        {/* 搜索表单 */}
        {(showSearchForm || !isMobile) && (
        <Card
          size="small"
          title={isMobile ? '筛选条件' : '搜索条件'}
          style={{ marginBottom: 16 }}
          styles={{ body: { paddingBottom: 0 } }}
        >
          <Form
            form={searchForm}
            onFinish={handleSearch}
            layout={isMobile ? 'vertical' : 'inline'}
            colon={false}
            className={isMobile ? 'search-form-mobile' : ''}
          >
            <Row gutter={[12, 0]} style={{ width: '100%' }}>
              <Col xs={12} sm={12} md={6}>
                <Form.Item
                  name="id"
                  label="ID"
                  style={{ marginBottom: 8 }}
                >
                  <Input
                    placeholder="输入ID"
                    allowClear
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Form.Item
                  name="deviceCode"
                  label="设备代码"
                  style={{ marginBottom: 8 }}
                >
                  <Input
                    placeholder="输入设备代码"
                    allowClear
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Form.Item
                  name="categoryId"
                  label="所属目录"
                  style={{ marginBottom: 8 }}
                >
                  <Select
                    placeholder="选择目录"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    style={{ width: '100%' }}
                  >
                    {flattenCategories(categories).map(category => (
                      <Option key={category.id} value={category.id}>
                        {getCategoryPath(category, categories)}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Form.Item
                  name="userIdentity"
                  label="用户身份"
                  style={{ marginBottom: 8 }}
                >
                  <Input
                    placeholder="输入用户身份"
                    allowClear
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[12, 0]} style={{ width: '100%' }}>
              <Col xs={12} sm={12} md={6}>
                <Form.Item
                  name="description"
                  label="描述"
                  style={{ marginBottom: 8 }}
                >
                  <Input
                    placeholder="输入描述关键词"
                    allowClear
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Form.Item
                  name="policyId"
                  label="管控措施"
                  style={{ marginBottom: 8 }}
                >
                  <Select
                    placeholder="选择管控措施"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    style={{ width: '100%' }}
                  >
                    {policies.map(policy => (
                      <Option key={policy.id} value={policy.id}>
                        {policy.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Form.Item
                  name="versionId"
                  label="版本"
                  style={{ marginBottom: 8 }}
                >
                  <Select
                    placeholder="选择版本"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    style={{ width: '100%' }}
                  >
                    {versions.map(version => (
                      <Option key={version.id} value={version.id}>
                        v{version.versionCode}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Form.Item
                  name="status"
                  label="状态"
                  style={{ marginBottom: 8 }}
                >
                  <Select
                    placeholder="选择状态"
                    allowClear
                    style={{ width: '100%' }}
                  >
                    <Option value="online">在线</Option>
                    <Option value="recent">最近活跃</Option>
                    <Option value="offline">离线</Option>
                    <Option value="disabled">已禁用</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[12, 0]} style={{ width: '100%' }}>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="dateRange"
                  label="最后通信时间"
                  style={{ marginBottom: 8 }}
                >
                  <RangePicker
                    style={{ width: '100%' }}
                    placeholder={['开始日期', '结束日期']}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={16}>
                <Form.Item label={isMobile ? undefined : ' '} style={{ marginBottom: 8 }}>
                  <Space>
                    <Button
                      type="primary"
                      icon={<SearchOutlined />}
                      htmlType="submit"
                    >
                      搜索
                    </Button>
                    <Button
                      icon={<ClearOutlined />}
                      onClick={handleResetSearch}
                    >
                      重置
                    </Button>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>
        )}
        <div className="mobile-table-wrapper">
        <Table
          columns={isMobile ? mobileColumns : columns}
          dataSource={filteredDevices}
          rowKey="id"
          loading={loading}
          size={isMobile ? 'small' : 'middle'}
          scroll={{ x: isMobile ? 500 : undefined }}
          pagination={{
            defaultPageSize: isMobile ? 20 : 100,
            pageSizeOptions: isMobile ? ['20', '50', '100'] : ['50', '100', '200', '500'],
            showSizeChanger: true,
            showQuickJumper: !isMobile,
            showTotal: (total, range) => isMobile ? `${total}条` : `第 ${range?.[0]}-${range?.[1]} 条，共 ${total} 条`,
            size: isMobile ? 'small' : 'default'
          }}
          className="device-table"
        />
        </div>
      </Card>

      <Modal
        title={editingDevice ? '编辑设备' : '注册新设备'}
        open={modalVisible}
        onCancel={handleCancel}
        footer={null}
        width={isMobile ? '95%' : 700}
        style={{ top: isMobile ? 20 : undefined }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          preserve={false}
        >
          <Form.Item
            label="设备代码"
            name="deviceCode"
            rules={[
              { required: true, message: '请输入设备代码' },
              { min: 3, message: '设备代码至少3个字符' }
            ]}
          >
            <Input
              placeholder="请输入唯一的设备代码"
              disabled={!!editingDevice}
            />
          </Form.Item>

          <Form.Item
            label="所属目录"
            name="categoryId"
            rules={[{ required: true, message: '请选择设备目录' }]}
          >
            <Select placeholder="请选择设备目录">
              {flattenCategories(categories).map(category => (
                <Option key={category.id} value={category.id}>
                  {getCategoryPath(category, categories)}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="管控措施"
            name="policyId"
          >
            <Select placeholder="请选择管控措施（可留空）" allowClear>
              {policies.map(policy => (
                <Option key={policy.id} value={policy.id}>
                  {policy.name} {policy.description && `- ${policy.description}`}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="版本"
            name="versionId"
          >
            <Select placeholder="请选择版本（可留空）" allowClear>
              {versions.map(version => (
                <Option key={version.id} value={version.id}>
                  v{version.versionCode} {version.description && `- ${version.description}`}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="用户身份"
            name="userIdentity"
          >
            <Input placeholder="请输入用户身份标识（可选）" />
          </Form.Item>

          <Form.Item
            label="设备描述"
            name="description"
          >
            <Input.TextArea
              rows={3}
              placeholder="请输入设备描述信息（可选）"
            />
          </Form.Item>

          {editingDevice && (
            <Form.Item
              label="设备状态"
              name="disabled"
              valuePropName="checked"
            >
              <Switch
                checkedChildren="已禁用"
                unCheckedChildren="已启用"
                style={{ background: form.getFieldValue('disabled') ? '#ff4d4f' : '#52c41a' }}
              />
            </Form.Item>
          )}

          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={handleCancel}>
                取消
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
              >
                {editingDevice ? '更新设备' : '注册设备'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 批量导入组件 */}
      <DeviceImport
        visible={importModalVisible}
        onClose={() => setImportModalVisible(false)}
        onSuccess={handleImportSuccess}
      />
    </div>
  );
};

export default Devices;
